import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Card, CardContent, Chip, Grid, Table, TableBody, TableCell,
  TableHead, TableRow, Typography, makeStyles,
} from '@material-ui/core'
import api from '../../api/client'
import { toast } from '../../api/toast'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'

const useStyles = makeStyles(() => ({
  root: { marginBottom: 18 },
  head: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 },
  title: { fontWeight: 800, color: '#1a237e', fontSize: 15 },
  period: { display: 'flex', gap: 6 },
  stat: { borderRadius: 14, padding: '14px 16px', color: '#fff', height: '100%' },
  statNum: { fontSize: 24, fontWeight: 800, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' },
  statLbl: { fontSize: 11.5, opacity: .9, marginTop: 2 },
  barRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, fontSize: 11.5 },
  barDate: { flex: '0 0 74px', color: '#546e7a', fontVariantNumeric: 'tabular-nums' },
  barTrack: { flex: 1, height: 12, background: '#eef2f8', borderRadius: 6, overflow: 'hidden', display: 'flex' },
  barOk: { background: 'linear-gradient(90deg,#059669,#10b981)', height: '100%' },
  barFail: { background: 'linear-gradient(90deg,#b91c1c,#ef4444)', height: '100%' },
  barNum: { flex: '0 0 92px', textAlign: 'left', color: '#37474f', fontWeight: 700 },
}))

const STATE_STYLE = {
  recorded: { bg: '#e8f5e9', fg: '#2e7d32' },
  initiated: { bg: '#fff8e1', fg: '#b26a00' },
  gateway_ok: { bg: '#e3f2fd', fg: '#1565c0' },
  gateway_failed: { bg: '#ffebee', fg: '#b71c1c' },
  failed_processing: { bg: '#fce4ec', fg: '#ad1457' },
}

const jdate = (iso) => {
  if (!iso) return '—'
  const s = gregorianToJalali(iso.slice(0, 10))
  return toFarsiDigits(s.slice(0, 10))
}

// تاریخ + ساعت (به وقت محلی مرورگر)
const jdateTime = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const pad2 = (n) => String(n).padStart(2, '0')
  const localDate = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
  const j = toFarsiDigits(gregorianToJalali(localDate).slice(0, 10))
  return `${j} — ${toFarsiDigits(`${pad2(d.getHours())}:${pad2(d.getMinutes())}`)}`
}

const STATS = [
  { key: 'total', title: 'اقدام به پرداخت', grad: 'linear-gradient(135deg,#1a237e,#3949ab)' },
  { key: 'recorded', title: 'پرداختِ موفق', grad: 'linear-gradient(135deg,#059669,#10b981)' },
  { key: 'failed', title: 'ناموفق', grad: 'linear-gradient(135deg,#b91c1c,#ef4444)' },
  { key: 'pending', title: 'نیمه‌کاره (بدون بازگشت)', grad: 'linear-gradient(135deg,#b45309,#f59e0b)' },
]

export default function PaymentAttemptsPanel() {
  const classes = useStyles()
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    api.get('users/admin/payment-attempts/', { params: { days } })
      .then((r) => setData(r.data))
      .catch(() => toast('خطا در دریافت آمار اقدام‌ها', 'error'))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [days])

  const daily = useMemo(() => (data?.daily || []).slice(-14).reverse(), [data])
  const maxDaily = useMemo(
    () => Math.max(1, ...daily.map((d) => d.total)), [daily]
  )

  const s = data?.summary || {}

  return (
    <Card className={classes.root} elevation={0} style={{ border: '1px solid #e6e9f6', borderRadius: 16 }}>
      <CardContent>
        <Box className={classes.head}>
          <Typography className={classes.title}>آمار اقدام به پرداخت</Typography>
          <Box style={{ flex: 1 }} />
          <Box className={classes.period}>
            {[7, 30, 90].map((d) => (
              <Chip
                key={d}
                size="small"
                label={`${toFarsiDigits(d)} روز`}
                onClick={() => setDays(d)}
                style={{
                  cursor: 'pointer', fontWeight: 700,
                  background: days === d ? '#1a237e' : '#eef1ff',
                  color: days === d ? '#fff' : '#283593',
                }}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={2} style={{ marginBottom: 12 }}>
          {STATS.map((k) => (
            <Grid item xs={6} sm={3} key={k.key}>
              <Box className={classes.stat} style={{ background: k.grad }}>
                <div className={classes.statNum}>{toFarsiDigits(s[k.key] || 0)}</div>
                <div className={classes.statLbl}>{k.title}</div>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box display="flex" flexWrap="wrap" style={{ gap: 10, marginBottom: 12, fontSize: 12.5 }}>
          <Chip size="small" label={`نرخ موفقیت: ${toFarsiDigits(s.rate || 0)}٪`} style={{ background: '#e8f5e9', color: '#2e7d32', fontWeight: 800 }} />
          <Chip size="small" label={`مبلغِ ثبت‌شده: ${toFarsiDigits(thousandsSeparate(s.amount_recorded || 0))} ریال`} style={{ background: '#e3f2fd', color: '#1565c0', fontWeight: 700 }} />
          <Chip size="small" label={`مبلغِ کلِ اقدام‌ها: ${toFarsiDigits(thousandsSeparate(s.amount_total || 0))} ریال`} style={{ background: '#f1f5f9', color: '#334155', fontWeight: 700 }} />
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: '#37474f', marginBottom: 6 }}>
              روزهای اخیر (۱۴ روز)
            </Typography>
            {daily.length === 0 && <Typography style={{ fontSize: 12, color: '#8a92b2' }}>داده‌ای نیست</Typography>}
            {daily.map((d) => (
              <Box key={d.date} className={classes.barRow}>
                <span className={classes.barDate}>{jdate(d.date)}</span>
                <span className={classes.barTrack}>
                  <span className={classes.barOk} style={{ width: `${(d.recorded / maxDaily) * 100}%` }} />
                  <span className={classes.barFail} style={{ width: `${(d.failed / maxDaily) * 100}%` }} />
                </span>
                <span className={classes.barNum}>{toFarsiDigits(d.total)} اقدام</span>
              </Box>
            ))}
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography style={{ fontSize: 12.5, fontWeight: 800, color: '#37474f', marginBottom: 6 }}>
              آخرین اقدام‌ها
            </Typography>
            <div style={{ maxHeight: 300, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['زمان', 'کاربر', 'موبایل', 'مبلغ', 'وضعیت'].map((h) => (
                      <TableCell key={h} style={{ background: '#f1f5f9', fontWeight: 800, fontSize: 11.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.recent || []).map((a) => {
                    const st = STATE_STYLE[a.state] || { bg: '#eceff1', fg: '#455a64' }
                    return (
                      <TableRow key={a.id}>
                        <TableCell style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{jdateTime(a.created_at)}</TableCell>
                        <TableCell style={{ fontSize: 11.5 }}>{a.user || a.username || '—'}</TableCell>
                        <TableCell style={{ fontSize: 11.5, direction: 'ltr', textAlign: 'right' }}>{a.mobile ? toFarsiDigits('0' + a.mobile.replace(/^0/, '')) : '—'}</TableCell>
                        <TableCell style={{ fontSize: 11.5, fontWeight: 700 }}>{toFarsiDigits(thousandsSeparate(a.amount || 0))}</TableCell>
                        <TableCell>
                          <Chip size="small" label={a.state_label}
                            title={a.error || ''}
                            style={{ background: st.bg, color: st.fg, fontWeight: 700, fontSize: 10.5 }} />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {(!data?.recent || data.recent.length === 0) && (
                    <TableRow><TableCell colSpan={5} align="center" style={{ fontSize: 12, color: '#8a92b2' }}>اقدامی ثبت نشده</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
